// Turns what a person types ("125.50", "1,250", "AED 99") into integer minor units for a currency,
// and back. Pure string maths: no floating point, so 0.07 never becomes 6.999999.
import { minorUnitExponent, type CurrencyCode } from '@ecobox/domain';

export type ParsedMoney = { ok: true; minor: number } | { ok: false; error: string };

/**
 * `"125.50"` with AED → `{ ok: true, minor: 12550 }`. Accepts thousands commas, spaces and a
 * leading currency code or symbol. An empty string is not an amount: the caller decides.
 */
export function parseMoneyText(text: string, currency: CurrencyCode): ParsedMoney {
  const exponent = minorUnitExponent(currency);
  const cleaned = text
    .trim()
    .replace(new RegExp(`^${currency}`, 'i'), '')
    .replace(/[\s,]/g, '')
    .replace(/^[₱$€£]/, '');
  if (cleaned === '') return { ok: false, error: 'Enter an amount' };
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '.') {
    return { ok: false, error: 'Use numbers only, like 125.50' };
  }
  const [whole = '', fraction = ''] = cleaned.split('.');
  if (fraction.length > exponent) {
    return {
      ok: false,
      error:
        exponent === 0
          ? `${currency} has no decimals`
          : `${currency} has at most ${exponent} decimal places`,
    };
  }
  const digits = `${whole === '' ? '0' : whole}${fraction.padEnd(exponent, '0')}`.replace(
    /^0+(?=\d)/,
    '',
  );
  const minor = Number(digits);
  if (!Number.isSafeInteger(minor)) return { ok: false, error: 'That amount is too large' };
  return { ok: true, minor };
}

/** `12550`, AED → `"125.50"` (plain digits, for an input box; never parsed back from display text). */
export function minorToText(minor: number, currency: CurrencyCode): string {
  const exponent = minorUnitExponent(currency);
  const negative = minor < 0;
  const digits = String(Math.abs(minor)).padStart(exponent + 1, '0');
  const whole = digits.slice(0, digits.length - exponent);
  const fraction = digits.slice(digits.length - exponent);
  return `${negative ? '-' : ''}${whole}${exponent > 0 ? `.${fraction}` : ''}`;
}
